import React, { useState, useRef, useEffect } from 'react';

export interface PondOption {
  id: string;
  name: string;
}

interface PondMultiSelectProps {
  ponds: PondOption[];
  selectedPondIds: string[];
  onChange: (pondIds: string[]) => void;
  maxSelection?: number;
  label?: string;
  className?: string;
}

export const PondMultiSelect: React.FC<PondMultiSelectProps> = ({
  ponds,
  selectedPondIds,
  onChange,
  maxSelection = 3,
  label = 'Select Ponds',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedPonds = ponds.filter(p => selectedPondIds.includes(p.id));
  const isMaxSelected = selectedPondIds.length >= maxSelection;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (pondId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    // Prevent event from bubbling and closing dropdown
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log('handleToggle called', { pondId, selectedPondIds, isSelected: selectedPondIds.includes(pondId) });

    const isSelected = selectedPondIds.includes(pondId);

    if (isSelected) {
      console.log('Deselecting pond', pondId);
      onChange(selectedPondIds.filter(id => id !== pondId));
    } else {
      if (selectedPondIds.length < maxSelection) {
        console.log('Selecting pond', pondId);
        onChange([...selectedPondIds, pondId]);
      } else {
        console.log('Max selection reached', maxSelection);
      }
    }

    // Keep dropdown open after selection - don't call setIsOpen(false)
  };

  const handleClearAll = () => {
    onChange([]);
  };

  // Color mapping for up to 3 ponds
  const pondColors = ['bg-emerald-500', 'bg-blue-500', 'bg-amber-500'];
  const getPondColor = (pondId: string) => {
    const index = selectedPondIds.indexOf(pondId);
    return index >= 0 ? pondColors[index] : 'bg-gray-300';
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} <span className="text-xs text-gray-500">(max {maxSelection})</span>
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-left shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1.5">
            {selectedPonds.length === 0 ? (
              <span className="text-sm text-gray-500">Select ponds to compare...</span>
            ) : (
              selectedPonds.map((pond) => (
                <span
                  key={pond.id}
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white"
                  style={{ backgroundColor: getPondColor(pond.id).replace('bg-', '#') === 'bg-emerald-500' ? '#10B981' : getPondColor(pond.id).replace('bg-', '#') === 'bg-blue-500' ? '#3B82F6' : '#F59E0B' }}
                >
                  {pond.name}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(pond.id, e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        handleToggle(pond.id, e);
                      }
                    }}
                    className="hover:bg-white/20 rounded-sm p-0.5 cursor-pointer"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </span>
                </span>
              ))
            )}
          </div>
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-10 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg">
          <div className="max-h-60 overflow-auto py-1">
            {ponds.map((pond) => {
              const isSelected = selectedPondIds.includes(pond.id);
              const isDisabled = !isSelected && isMaxSelected;

              return (
                <button
                  key={pond.id}
                  type="button"
                  onClick={(e) => {
                    if (!isDisabled) {
                      handleToggle(pond.id, e);
                    }
                  }}
                  disabled={isDisabled}
                  className={`
                    flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors
                    ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}
                    ${isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'}
                  `}
                >
                  <div
                    className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && (
                      <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="flex-1 text-left">{pond.name}</span>
                  {isSelected && (
                    <div
                      className={`h-3 w-3 rounded-full ${getPondColor(pond.id)}`}
                      title="Chart color"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {selectedPonds.length > 0 && (
            <div className="border-t border-slate-200/50 px-3 py-2">
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Selected count indicator */}
      {selectedPonds.length > 0 && (
        <p className="mt-1 text-xs text-gray-600">
          {selectedPonds.length} of {maxSelection} ponds selected
        </p>
      )}
    </div>
  );
};
